import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";

import {
  UIActionModal,
  UIChangeSummary,
  UIEmptyState,
  UIIcon,
  UIUserInfo,
} from "../../../ui";
import { clx } from "../../../../utils/Helpers";
import messages from "../../../../assets/locale/messages";

const ChangeSupervisorModal = ({
  open,
  currentSupervisorId,
  currentSupervisorName,
  options,
  onClose,
  onConfirm,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { zoneApplicants, shared } = messages[lang];
  const t = zoneApplicants.supervisorModal;

  const [selected, setSelected] = useState("");

  // Nothing is preselected: the confirm action stays disabled until a
  // supervisor is picked. The list never holds the zone's own supervisor, so
  // any selection is a real change.
  useEffect(() => {
    if (open) setSelected("");
  }, [open]);

  const isChange = Boolean(currentSupervisorId);
  const selectedOption = options.find((option) => option.value === selected);

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      variant="info"
      title={isChange ? zoneApplicants.changeSupervisor : t.assignTitle}
      className="change-supervisor-modal"
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={isChange ? t.changeAndSave : shared.save}
          confirmVariant="approve"
          confirmDisabled={!selectedOption}
          onCancel={onClose}
          onConfirm={() => onConfirm(selected)}
        />
      }
    >
      {isChange && selectedOption && (
        <UIChangeSummary
          tone="outline"
          separator="swap"
          label={t.replacing}
          items={[currentSupervisorName, selectedOption.label]}
        />
      )}

      {options.length ? (
        <Box
          className="change-supervisor-modal__list"
          role="radiogroup"
          aria-label={
            isChange ? zoneApplicants.changeSupervisor : t.assignTitle
          }
        >
          {options.map((option) => {
            const isSelected = option.value === selected;

            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={clx(
                  "change-supervisor-modal__option",
                  isSelected && "change-supervisor-modal__option--selected"
                )}
                onClick={() => setSelected(option.value)}
              >
                <UIUserInfo
                  name={option.label}
                  className="change-supervisor-modal__user"
                />
              </button>
            );
          })}
        </Box>
      ) : (
        <UIEmptyState
          classNames={{ root: "change-supervisor-modal__empty" }}
          icon={<UIIcon name="twoUsers" width={24} height={24} />}
          title={t.noAvailableSupervisor}
        />
      )}
    </UIActionModal>
  );
};

ChangeSupervisorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Stringified id of the supervisor currently on the zone, if any. */
  currentSupervisorId: PropTypes.string,
  currentSupervisorName: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ChangeSupervisorModal.defaultProps = {
  currentSupervisorId: "",
  currentSupervisorName: null,
  options: [],
};

export default ChangeSupervisorModal;
